
import { ContentType } from "@/api/scode/scode_params";
import PrdVideoCardListCore, { PrdVideoCardListCoreRef } from "@/pages/promote/components/recommend/PrdVideoCardListCore";
import { useCommonContext } from "@/utils/context/common";
import { usePageTitle } from "@/utils/page_utils";
import { getCurrentVcScene } from "@/utils/sceneProvider";
import { setH5ShareCardInfo } from "@/utils/share/common";
import { ProductModel } from "@akc/biz-api";
import { useMount, useUnmount } from "ahooks";
import React, { useMemo, useRef, useState } from "react";
import { useActivate, useUnactivate } from "react-activation";
import { useSearchParams } from "react-router-dom";
import TopNavBar from "./components/TopNavBar";


/**
 * 商品视频大卡列表页面
 * activitySpuId 商品活动spuId
 * vcScene 推荐场景， "personVideoCard": 逛一逛推荐场景，"videoCard_other_scene"：普通商卡商详进的商品视频大卡场景，"gyg_discover_page": 逛一逛发现页素材商品进的商品视频大卡场景
 * theme 主题色类型，'dark' | 'light'，默认light
 * showVideoBtmGuide 是否展示视频底部引导文案“上滑看更多精彩视频”，1为展示，0为不展示
 * showVideoBottomBar 是否展示视频底部操作栏（加入购物车、立即购买、转发赚钱等），1为展示，0为不展示
 * hideForwardBtn 是否隐藏转发按钮，1为隐藏，0或空为不隐藏
 * materialNo 初始商品素材编号,用于在分享踩链后，踩链者和分享者看到的同一个商品的同一个视频
 * liveNo 直播间编号，用于计算直播间GMV，从商详跳过来的会带此参数
 */
const VideoCardList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const activitySpuId = searchParams.get("activitySpuId") || "";
  const initMaterialNo = searchParams.get("materialNo") || "";
  // 优先使用url中的vcScene，没有则使用sessionStorage中的vcScene
  const vcSceneRef = useRef<string>(searchParams.get("vcScene") || getCurrentVcScene());
  // todo:已有入口都要加showVideoBtmGuide\showVideoBottomBar参数
  const [showVideoBtmGuide, setShowVideoBtmGuide] = useState(searchParams.get("showVideoBtmGuide") === "1");
  const showVideoBottomBar = searchParams.get("showVideoBottomBar") === "1";
  const hideForwardBtn = searchParams.get("hideForwardBtn") === "1";
  const theme = (searchParams.get("theme") || "light") as "dark" | "light";
  const liveNo = searchParams.get("liveNo") || "";
  //分享使用的扩展字段
  const shareContentExtra=useMemo(() => {
    return {
      pageName: "/mx-shop-micro/video-card-list",//用于分享的页面名称，用于分享后的踩链定位
      showVideoBtmGuide:  searchParams.get("showVideoBtmGuide") || "",
      showVideoBottomBar: searchParams.get("showVideoBottomBar") || "",
      hideForwardBtn: searchParams.get("hideForwardBtn") || "",
      theme:  searchParams.get("theme") || "",
      materialNo:""
    };
  }, [searchParams]);
    
  
  const [isBackground, setIsBackground] = useState(false);
  const [notifyFollowProduct, setNotifyFollowProduct] = useState<{
    isFollow?: boolean;
    spuId?: string;
  } | null>(null);

  const [currentProduct, setCurrentProduct] = useState<ProductModel | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [searchRequestId, setSearchRequestId] = useState<string>("");

  const { userVisitInfo, track } = useCommonContext();
  const prdVideoCardListCoreRef = React.useRef<PrdVideoCardListCoreRef>(null);



  usePageTitle("商品视频");


  const handleTopNavBarShare = () => {
    prdVideoCardListCoreRef.current?.showPoster();
  };

  // 点击导航栏购物车
  const handleTopNavBarCart = () => {
    if (track && track.track) {
      track.track("resource_click", {
        page_name: "视频大卡",
        btn_name: "购物车",
        resource_type: "视频",
        resource_rank: currentIndex + 1,
        domain: "视频大卡",
        activity_id: currentProduct?.activityId,
        brand_id: currentProduct?.brandInfo?.brandId,
        activity_spu_code: currentProduct?.activitySpuId,
        sku_id: currentProduct?.selectSkuId,
        material_id: currentProduct?.materialRelationProductInfoVO?.materialNo,
        search_request_id: searchRequestId,
      });
    }
  };

  // 点击导航栏搜索
  const handleTopNavBarSearch = () => {
    if (track && track.track) {
      track.track("resource_click", {
        page_name: "视频大卡",
        btn_name: "搜索",
        resource_type: "视频",
        resource_rank: currentIndex + 1,
        domain: "视频大卡",
        activity_id: currentProduct?.activityId,
        brand_id: currentProduct?.brandInfo?.brandId,
        activity_spu_code: currentProduct?.activitySpuId,
        sku_id: currentProduct?.selectSkuId,
        material_id: currentProduct?.materialRelationProductInfoVO?.materialNo,
        search_request_id: searchRequestId,
      });
    }
  };






  const handleIndexChange = (product: ProductModel, index: number, requestId: string) => {
    if(product?.activityId && product?.activityId===currentProduct?.activityId) {
      return;
    }
    // 仅第一个商品展示引导
    if (index !== 0) {
      setShowVideoBtmGuide(false);
    }
    setCurrentProduct(product);
    setCurrentIndex(index);
    if (requestId) {
      setSearchRequestId(requestId);
    }

    setH5ShareCardInfo({
      contentType: ContentType.spuProduct,
      contentId: product.activitySpuId ?? "",
      contentDetailId: product.selectSkuId ?? "",
      contentTypeConfig: "1",
      contentExtra: {
        ...(shareContentExtra || {}),
        hasVideo: product.productVideoCardVO?.type==="VIDEO",
        materialNo: product.productVideoCardVO?.materialNo,
      },
      posterInfo: {},
      page_name: "视频大卡",
    });
  };

  useMount(() => {
    setIsBackground(false);
  });

  useUnmount(() => {
    setIsBackground(true);
  });

  useActivate(() => {
    setIsBackground(false);
    try {
      const info = localStorage.getItem("notifyFollowProduct");
      if (info) {
        console.log("notifyFollowProduct", info);
        setNotifyFollowProduct(JSON.parse(info));
        localStorage.removeItem("notifyFollowProduct");
      }
    } catch (error) {
      console.error("获取 notifyFollowProduct 失败:", error);
    }
  });

  useUnactivate(() => {
    setIsBackground(true);
    setNotifyFollowProduct(null);
  });

  return (
    <div>
      {/* 自定义导航栏 */}
      <TopNavBar
        onShareClick={handleTopNavBarShare}
        onCartClick={handleTopNavBarCart}
        onSearchClick={handleTopNavBarSearch}
      />

      <PrdVideoCardListCore
          ref={prdVideoCardListCoreRef}
          isBackground={isBackground}
          notifyFollowProduct={notifyFollowProduct || undefined}
          activitySpuId={activitySpuId}
          initMaterialNo={initMaterialNo}
          vcScene={vcSceneRef.current}
          showVideoBtmGuide={showVideoBtmGuide}
          showVideoBottomBar={showVideoBottomBar}
          hideForwardBtn={hideForwardBtn}
          theme={theme}
          liveNo={liveNo}
          trackDataCommon={{
            page_name: "视频大卡",
            domain: "视频大卡",
          }}
          shareContentExtra={shareContentExtra}
          onIndexChange={handleIndexChange}
        />
    </div>
  );
};

export default VideoCardList;
